import { Action } from 'redux';
import { SagaIterator } from 'redux-saga';
import { takeLatest, put, all, take, call, select } from 'redux-saga/effects';
import { find, get } from 'lodash';

import { getPlans, updatePlan } from '@api/account';
import { Plan } from '@api/types';
import { fetchData as fetchAccountData } from '@redux/ducks/accountDetails';
import { ACCOUNT_SET, getAccount } from '@redux/ducks/global';
import {
  fetchData,
  FETCH_DATA_STARTED,
  fetchDataSucceeded,
  fetchDataFailed,
  CANCEL_DOWNGRADE,
  LOAD,
  getState,
} from '@redux/ducks/account/pricingPlan/pricingPlans';
import { PlanPeriod } from '@redux/ducks/account/pricingPlan/interfaces';

// Tasks

export function* loadTask() {
  const { isDataLoaded } = yield select(getState);

  if (isDataLoaded) {
    return;
  }

  yield put(fetchData());
}

export function* fetchDataTask() {
  try {
    /**
     * It's required to force load data from the backend to avoid any issues with contract length
     * while switching from Monthly to Yearly pricing plan.
     */
    yield put(fetchAccountData({ forceUpdate: true }));

    yield take(ACCOUNT_SET);

    const { id, allowMonthlyPricing } = yield select(getAccount);

    const plans = yield call(getPlans, id);

    const currentPlan = find(plans, { current: true });
    const isCurrentPlanYearly = !!get(currentPlan, PlanPeriod.Yearly);

    const canSwitchPeriod = currentPlan ? !isCurrentPlanYearly : allowMonthlyPricing;

    const planPeriod = !currentPlan || isCurrentPlanYearly ? PlanPeriod.Yearly : PlanPeriod.Monthly;

    yield put(
      fetchDataSucceeded({
        plans,
        currentPlan,
        isCurrentPlanYearly,
        canSwitchPeriod,
        planPeriod,
      }),
    );
  } catch (_err) {
    yield put(fetchDataFailed());
  }
}

interface CancelDowngradeAction extends Action {
  payload: Plan;
}

export function* cancelDowngradeTask({ payload: plan }: CancelDowngradeAction): SagaIterator {
  const { id: accountId } = yield select(getAccount);

  yield call(updatePlan, accountId, plan.id, false);

  yield put(fetchData());
}

// Watchers

export function* watchLoad() {
  yield takeLatest(LOAD, loadTask);
}

export function* watchFetchData() {
  yield takeLatest(FETCH_DATA_STARTED, fetchDataTask);
}

export function* watchCancelDowngrade() {
  yield takeLatest<CancelDowngradeAction>(CANCEL_DOWNGRADE, cancelDowngradeTask);
}

// Saga

export default function* pricingPlanSaga() {
  yield all([watchLoad(), watchFetchData(), watchCancelDowngrade()]);
}
